import type { ForqSupabaseClient } from './client';
import {
  normalizeWarehouseLocationInput,
  normalizeWarehouseMovementInput,
  normalizeWarehouseStockSearchInput,
  type OpenWarehouseCountInput,
  type PostWarehouseCountInput,
  type PostWarehouseMovementInput,
  type SaveWarehouseCountLineInput,
  type SaveWarehouseLocationInput,
  type WarehouseArticleOption,
  type WarehouseCountLineRecord,
  type WarehouseCountSessionRecord,
  type WarehouseGateway,
  type WarehouseLocationRecord,
  type WarehouseMovementRecord,
  type WarehouseOverview,
  type WarehouseReplenishmentRecord,
  type WarehouseStockPage,
  type WarehouseStockSearchInput,
} from '@forq/warehouse-data';
import { SupabaseOperationError } from './errors';

function messageOf(error: { readonly message: string } | null): string {
  return error?.message ?? 'Unknown Supabase error.';
}

function arrayOf<T>(value: unknown, operation: string): readonly T[] {
  if (!Array.isArray(value)) throw new SupabaseOperationError(operation, 'The response is not an array.');
  return value as readonly T[];
}

function objectOf<T>(value: unknown, operation: string): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError(operation, 'The response is not an object.');
  }
  return value as T;
}

export class SupabaseWarehouseGateway implements WarehouseGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async getOverview(tenantId: string): Promise<WarehouseOverview> {
    const { data, error } = await this.client.rpc('warehouse_overview', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('warehouse.overview', messageOf(error));
    return objectOf<WarehouseOverview>(data, 'warehouse.overview');
  }

  async listLocations(tenantId: string, includeInactive = false): Promise<readonly WarehouseLocationRecord[]> {
    const { data, error } = await this.client.rpc('warehouse_location_catalog', {
      p_tenant_id: tenantId,
      p_include_inactive: includeInactive,
    });
    if (error) throw new SupabaseOperationError('warehouse.locations', messageOf(error));
    return arrayOf<WarehouseLocationRecord>(data ?? [], 'warehouse.locations');
  }

  async saveLocation(input: SaveWarehouseLocationInput): Promise<WarehouseLocationRecord> {
    const normalized = normalizeWarehouseLocationInput(input);
    const { data, error } = await this.client.rpc('warehouse_location_save', {
      p_tenant_id: normalized.tenantId,
      p_payload: {
        locationId: normalized.locationId ?? null,
        code: normalized.code,
        name: normalized.name,
        description: normalized.description ?? '',
        active: normalized.active ?? true,
        defaultLocation: normalized.defaultLocation ?? false,
      },
    });
    if (error) throw new SupabaseOperationError('warehouse.location.save', messageOf(error));
    return objectOf<WarehouseLocationRecord>(data, 'warehouse.location.save');
  }

  async searchArticleOptions(tenantId: string, query = ''): Promise<readonly WarehouseArticleOption[]> {
    const { data, error } = await this.client.rpc('warehouse_article_options', {
      p_tenant_id: tenantId,
      p_query: query.trim(),
      p_limit: 50,
    });
    if (error) throw new SupabaseOperationError('warehouse.articles', messageOf(error));
    return arrayOf<WarehouseArticleOption>(data ?? [], 'warehouse.articles');
  }

  async searchStock(input: WarehouseStockSearchInput): Promise<WarehouseStockPage> {
    const normalized = normalizeWarehouseStockSearchInput(input);
    const { data, error } = await this.client.rpc('warehouse_stock_search', {
      p_tenant_id: normalized.tenantId,
      p_query: normalized.query,
      p_filter: normalized.filter,
      p_location_id: normalized.locationId === 'all' ? null : normalized.locationId,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('warehouse.stock.search', messageOf(error));
    return objectOf<WarehouseStockPage>(data, 'warehouse.stock.search');
  }

  async listMovements(tenantId: string, limit = 100): Promise<readonly WarehouseMovementRecord[]> {
    const { data, error } = await this.client.rpc('warehouse_movement_list', {
      p_tenant_id: tenantId,
      p_limit: Math.min(250, Math.max(1, Math.trunc(limit))),
    });
    if (error) throw new SupabaseOperationError('warehouse.movements', messageOf(error));
    return arrayOf<WarehouseMovementRecord>(data ?? [], 'warehouse.movements');
  }

  async postMovement(input: PostWarehouseMovementInput): Promise<WarehouseMovementRecord> {
    const normalized = normalizeWarehouseMovementInput(input);
    const { data, error } = await this.client.rpc('warehouse_post_movement', {
      p_tenant_id: normalized.tenantId,
      p_payload: {
        type: normalized.type,
        itemId: normalized.itemId,
        quantity: normalized.quantity,
        sourceLocationId: normalized.sourceLocationId ?? null,
        targetLocationId: normalized.targetLocationId ?? null,
        reason: normalized.reason,
        reference: normalized.reference ?? '',
      },
    });
    if (error) throw new SupabaseOperationError('warehouse.movement.post', messageOf(error));
    return objectOf<WarehouseMovementRecord>(data, 'warehouse.movement.post');
  }

  async listReplenishment(tenantId: string): Promise<readonly WarehouseReplenishmentRecord[]> {
    const { data, error } = await this.client.rpc('warehouse_replenishment', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('warehouse.replenishment', messageOf(error));
    return arrayOf<WarehouseReplenishmentRecord>(data ?? [], 'warehouse.replenishment');
  }

  async openCountSession(input: OpenWarehouseCountInput): Promise<WarehouseCountSessionRecord> {
    const { data, error } = await this.client.rpc('warehouse_count_open', {
      p_tenant_id: input.tenantId,
      p_payload: { locationId: input.locationId ?? null, note: input.note?.trim() ?? '' },
    });
    if (error) throw new SupabaseOperationError('warehouse.count.open', messageOf(error));
    return objectOf<WarehouseCountSessionRecord>(data, 'warehouse.count.open');
  }

  async listCountSessions(tenantId: string): Promise<readonly WarehouseCountSessionRecord[]> {
    const { data, error } = await this.client.rpc('warehouse_count_list', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('warehouse.count.list', messageOf(error));
    return arrayOf<WarehouseCountSessionRecord>(data ?? [], 'warehouse.count.list');
  }

  async getCountSession(tenantId: string, sessionId: string): Promise<WarehouseCountSessionRecord> {
    const { data, error } = await this.client.rpc('warehouse_count_get', { p_tenant_id: tenantId, p_session_id: sessionId });
    if (error) throw new SupabaseOperationError('warehouse.count.get', messageOf(error));
    return objectOf<WarehouseCountSessionRecord>(data, 'warehouse.count.get');
  }

  async saveCountLine(input: SaveWarehouseCountLineInput): Promise<WarehouseCountLineRecord> {
    const { data, error } = await this.client.rpc('warehouse_count_save_line', {
      p_tenant_id: input.tenantId,
      p_session_id: input.sessionId,
      p_line_id: input.lineId,
      p_counted_quantity: input.countedQuantity,
    });
    if (error) throw new SupabaseOperationError('warehouse.count.save-line', messageOf(error));
    return objectOf<WarehouseCountLineRecord>(data, 'warehouse.count.save-line');
  }

  async postCountSession(input: PostWarehouseCountInput): Promise<WarehouseCountSessionRecord> {
    const { data, error } = await this.client.rpc('warehouse_count_post', { p_tenant_id: input.tenantId, p_session_id: input.sessionId });
    if (error) throw new SupabaseOperationError('warehouse.count.post', messageOf(error));
    return objectOf<WarehouseCountSessionRecord>(data, 'warehouse.count.post');
  }
}
